import { Component, OnDestroy, OnInit } from '@angular/core';
import { NotifyTemplateService } from './services/notify-template.service';
import { UntypedFormBuilder,UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { INotifyTemplate } from './models/notify-template.interface';
import { NotificationService } from '../shared/services/notification/notification.service';
import { NotificationType } from '../shared/models/notification-type';
import { UtilityService } from '../shared/services/utility/utility.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NotifyTemplateFormDialogComponent } from './notify-template-form-dialog/notify-template-form-dialog.component';
import { ErrorHandlerService } from '../shared/services/error-handler/error-handler.service';

@Component({
  selector: 'upsc-notify-templates',
  templateUrl: './notify-templates.component.html',
  styleUrls: ['./notify-templates.component.scss'],
})
export class NotifyTemplatesComponent implements OnInit, OnDestroy {
  public formGroup: UntypedFormGroup;
  public templates: INotifyTemplate[];

  private getNotifyTemplatesSubscription: Subscription;

  constructor(private formBuilder: UntypedFormBuilder,
              private notificationService: NotificationService,
              private utilityService: UtilityService,
              private errorHandlerService: ErrorHandlerService,
              private dialog: MatDialog,
              private notifyTemplateService: NotifyTemplateService) {
  }

  public ngOnInit() {
    this.formGroup = this.formBuilder.group({
      defaultTemplateId: [''],
    });

    this.monitorValueChanges();
    this.getNotifyTemplates();
  }

  public ngOnDestroy() {
    this.unsubscribeGetNotifyTemplates();
  }

  private monitorValueChanges() {
    this.monitorDefaultTemplateId();
  }

  private monitorDefaultTemplateId() {
    this.formGroup.controls.defaultTemplateId.valueChanges
      .subscribe(
        (value) => {
          const targetTemplate = this.templates.find(template => template.TemplateId === value);
          targetTemplate.isInProgress = true;

          this.notifyTemplateService.setPrimaryNotifyTemplate(value)
            .subscribe(
              (result) => {
                this.utilityService.delay(() => {
                  targetTemplate.isInProgress = false;
                });

                if (!result) {
                  this.notificationService.notify(
                    'The system failed to set the default template. Please try again later.',
                    'Failed setting the default template',
                    NotificationType.ERROR);

                  return;
                }

                this.templates.map(
                  (item) => {
                    item.IsPrimary = item.TemplateId === value;
                    return item;
                  },
                );
              },
            );
        },
      );
  }

  private unsubscribeGetNotifyTemplates() {
    if (!this.getNotifyTemplatesSubscription) {
      return;
    }

    this.getNotifyTemplatesSubscription.unsubscribe();
    this.getNotifyTemplatesSubscription = null;
  }

  private getNotifyTemplates() {
    this.unsubscribeGetNotifyTemplates();

    this.getNotifyTemplatesSubscription = this.notifyTemplateService.getNotifyTemplates()
      .subscribe(
        templates => this.handleGetNotifyTemplatesSuccess(templates),
        err => this.handleGetNotifyTemplatesFailure(err),
      );
  }

  private handleGetNotifyTemplatesSuccess(templates: INotifyTemplate[]) {
    this.templates = templates;

    if (!this.templates) {
      return;
    }

    const defaultTemplate = this.templates.find(template => template.IsPrimary);

    if (!defaultTemplate) {
      return;
    }

    this.formGroup.controls.defaultTemplateId.setValue(defaultTemplate.TemplateId, { emitEvent: false });
  }

  private handleGetNotifyTemplatesFailure(err) {
    this.notificationService.notify(
      this.errorHandlerService.getHttpErrorMessage(err),
      'Failed getting notify templates',
      NotificationType.ERROR);
  }

  public openTemplateCreationDialog(event) {
    event.preventDefault();

    const dialogConfig: MatDialogConfig = {
      disableClose: true,
      data: {},
    };

    const dialogRef = this.dialog.open(NotifyTemplateFormDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      (template: INotifyTemplate) => {
        // [MV3-1807] Refresh the whole list after a new template is added.
        // The current API doesn't provide a new template ID back after it is added.
        // Therefore, we need to refresh the list to get the ID of the newly added template.
        // Otherwise, actions such as setting it as a default template or removing it will not work properly
        // due to the lack of the template ID.
        if (!template || !template.TemplateId) {
          this.getNotifyTemplates();
          return;
        }

        if (!this.templates) {
          this.templates = [];
        }

        this.templates.push(template);
      },
    );
  }

  public onTemplateRemoved(template: INotifyTemplate) {
    this.templates = this.templates.filter(item => item.TemplateId !== template.TemplateId);
  }
}
